import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Mail, Loader2, ShieldCheck, RefreshCw } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Capture trial attribution params once on mount
  useEffect(() => {
    const kit_id = searchParams.get("kit_id");
    if (!kit_id) return;
    try {
      localStorage.setItem("myb_trial_attribution", JSON.stringify({
        kit_id,
        ref: searchParams.get("ref"),
        utm_source: searchParams.get("utm_source"),
        utm_medium: searchParams.get("utm_medium"),
        utm_campaign: searchParams.get("utm_campaign"),
      }));
    } catch { /* storage disabled */ }
  }, [searchParams]);

  // step: 1 = collect details, 2 = verify OTP, 3 = creating account
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [otp, setOtp] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [busy, setBusy] = useState(false);

  // tick countdown
  useEffect(() => {
    if (!otpExpiresAt) return;
    const tick = () => {
      const s = Math.max(0, Math.round((new Date(otpExpiresAt).getTime() - Date.now()) / 1000));
      setSecondsLeft(s);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [otpExpiresAt]);

  const requestOtp = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toast.error("Please fill name, email and a 6+ char password");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/otp/request", { email: form.email });
      setOtpExpiresAt(data.expires_at);
      setStep(2);
      toast.success(`Code sent to ${form.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code");
    } finally { setBusy(false); }
  };

  const verifyAndRegister = async () => {
    if (otp.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      await api.post("/otp/verify", { email: form.email, code: otp });
      setStep(3);
      const u = await register(form.name, form.email, form.password, form.phone);
      // Fire-and-forget trial attribution (do NOT block signup flow on failure)
      try {
        const raw = localStorage.getItem("myb_trial_attribution");
        if (raw) {
          const attr = JSON.parse(raw);
          if (attr?.kit_id) {
            api.post("/trial/attribute", attr).catch(() => {});
            localStorage.removeItem("myb_trial_attribution");
          }
        }
      } catch { /* ignore */ }
      toast.success(`Welcome to MakeYourBrand.IN, ${u.name}`);
      nav("/onboarding/profile");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
      setStep(2);
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/otp/request", { email: form.email });
      setOtpExpiresAt(data.expires_at);
      setOtp("");
      toast.success("New code sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white grid-bg flex items-center justify-center p-6">
      <div className="absolute top-6 left-6">
        <Link to="/" data-testid="auth-back-home" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
      </div>

      <div className="w-full max-w-md glass rounded-2xl p-8 fade-in-up">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-lg brand-gradient grid place-items-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <span className="font-heading font-black text-lg tracking-tight">
            MakeYour<span className="brand-gradient-text">Brand.IN</span>
          </span>
        </div>

        {step === 1 && (
          <>
            <h1 className="font-heading text-3xl font-black tracking-tight mb-2">Create your brand</h1>
            <p className="text-sm text-white/50 mb-6">Step 1 of 3 · Account details</p>
            <div className="space-y-4">
              <FormField label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="register-name-input" placeholder="Surajit Majhi" />
              <FormField label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="register-email-input" placeholder="you@brand.com" />
              <FormField label="Phone (with country code)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="register-phone-input" placeholder="+91 81006 71006" />
              <FormField label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="register-password-input" placeholder="At least 6 characters" />
              <button onClick={requestOtp} disabled={busy} data-testid="register-send-otp" className="w-full brand-gradient px-5 py-3 rounded-lg font-semibold text-sm hover:opacity-90 transition glow-orange disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Send verification code
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h1 className="font-heading text-3xl font-black tracking-tight mb-2">Verify your email</h1>
            <p className="text-sm text-white/50 mb-6">Step 2 of 3 · We sent a 6-digit code to <span className="text-white/80">{form.email}</span></p>
            <input
              type="text" inputMode="numeric" maxLength={6}
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="register-otp-input"
              autoFocus
              className="w-full bg-white/5 border border-white/10 focus:border-indigo-500/60 outline-none rounded-lg px-4 py-3 text-2xl font-mono tracking-[0.5em] text-center"
              placeholder="------"
            />
            <div className="mt-3 text-xs text-white/40 text-center" data-testid="register-otp-timer">
              {secondsLeft > 0 ? `Code expires in ${Math.floor(secondsLeft/60)}m ${secondsLeft%60}s` : "Code expired — request a new one"}
            </div>
            <button onClick={verifyAndRegister} disabled={busy || otp.length !== 6} data-testid="register-verify-otp"
              className="mt-5 w-full brand-gradient px-5 py-3 rounded-lg font-semibold text-sm hover:opacity-90 transition glow-orange disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Verify & create account
            </button>
            <div className="mt-4 flex items-center justify-between text-xs">
              <button onClick={() => setStep(1)} className="text-white/40 hover:text-white">← Edit details</button>
              <button onClick={resend} disabled={busy || secondsLeft > 0} data-testid="register-resend-otp" className="text-indigo-300 hover:text-indigo-200 disabled:opacity-30 inline-flex items-center gap-1">
                <RefreshCw className="h-3 w-3" /> Resend code
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-400 mb-4" />
            <div className="text-white/70">Creating your account…</div>
          </div>
        )}

        <div className="mt-8 text-sm text-white/50 text-center">
          Already a customer?{" "}
          <Link to="/login" data-testid="register-login-link" className="brand-gradient-text font-semibold">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, type = "text", value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-white/40">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required data-testid={testid}
        className="mt-2 w-full bg-white/5 border border-white/10 focus:border-indigo-500/60 outline-none rounded-lg px-4 py-3 text-sm"
        placeholder={placeholder} />
    </div>
  );
}
